import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, useList, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { useChannels } from "@/hooks/useChannels";
import { Users, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { channel_id: "", name: "", phone_number: "", email: "", company: "", tags: "", notes: "", opt_in_status: "unknown" };

export default function Contacts() {
  const channels = useChannels();
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch } = useList("/contacts");
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const channelName = (id) => channels.find((c) => c.id === id)?.name || "-";

  const openCreate = () => { setForm(EMPTY); setEditing(null); setDrawer(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...c, tags: (c.tags || []).join(", ") });
    setDrawer(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
      if (editing) {
        const { channel_id, source, ...upd } = payload;
        await api.put(`/contacts/${editing.id}`, upd);
        toast.success("Kontak berhasil diperbarui.");
      } else {
        await api.post("/contacts", payload);
        toast.success("Kontak berhasil dibuat.");
      }
      setDrawer(false);
      refetch();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kontak ${c.name || c.phone_number}?`)) return;
    try {
      await api.del(`/contacts/${c.id}`);
      toast.success("Kontak berhasil dihapus.");
      refetch();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div>
      <PageHeader title="Contacts" subtitle="Kontak tersimpan lintas channel milik Anda."
        action={<Button onClick={openCreate} className="bg-[#FFDA1A] hover:bg-[#F5CE00] text-[#111111]" data-testid="create-contact-button"><Plus className="w-4 h-4 mr-1.5" /> Kontak Baru</Button>} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari nama, nomor, email..." testid="contact-search" />
        <Select value={filters.channel_id || "all"} onValueChange={(v) => setFilters({ ...filters, channel_id: v === "all" ? "" : v })}>
          <SelectTrigger className="w-48 bg-white" data-testid="contact-channel-filter"><SelectValue placeholder="Semua channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua channel</SelectItem>
            {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.opt_in_status || "all"} onValueChange={(v) => setFilters({ ...filters, opt_in_status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="contact-optin-filter"><SelectValue placeholder="Opt-in" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua opt-in</SelectItem>
            <SelectItem value="opted_in">Opt-in</SelectItem>
            <SelectItem value="opted_out">Opt-out</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={Users} title="Belum ada kontak" description="Buat kontak manual atau simpan participant dari percakapan."
          action={<Button onClick={openCreate} className="bg-[#003399] hover:bg-[#002B80]"><Plus className="w-4 h-4 mr-1.5" /> Kontak Baru</Button>} />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>Nama</th>
                <th className={thClass}>Nomor</th>
                <th className={`${thClass} hidden md:table-cell`}>Channel</th>
                <th className={`${thClass} hidden lg:table-cell`}>Opt-in</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} onClick={() => openEdit(c)} className={trClass} data-testid={`contact-row-${c.id}`}>
                  <td className={tdFirstClass}>
                    <p className="font-medium text-[#111111]">{c.name || "Tanpa Nama"}</p>
                    {c.company && <p className="text-xs text-[#767676]">{c.company}</p>}
                  </td>
                  <td className={`${tdClass} font-mono text-[13px] text-[#003399]`}>{c.phone_number}</td>
                  <td className={`${tdClass} hidden md:table-cell`}>{channelName(c.channel_id)}</td>
                  <td className={`${tdClass} hidden lg:table-cell`}><StatusBadge status={c.opt_in_status} /></td>
                  <td className={`${tdClass} text-right`}>
                    <button onClick={(e) => { e.stopPropagation(); remove(c); }} className="text-[#767676] hover:text-[#CC0008] p-1 transition-colors" data-testid={`delete-contact-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={drawer} onClose={() => setDrawer(false)} title={editing ? "Edit Kontak" : "Kontak Baru"}
        description={editing ? "Perbarui data kontak." : "Tambah kontak manual ke channel."} testid="contact-drawer"
        footer={<>
          <Button variant="outline" onClick={() => setDrawer(false)} data-testid="contact-cancel">Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-[#003399] hover:bg-[#002B80]" data-testid="contact-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
          </Button>
        </>}>
        <div className="space-y-4">
          {!editing && (
            <div className="space-y-2">
              <Label>Channel *</Label>
              <Select value={form.channel_id} onValueChange={(v) => setForm({ ...form, channel_id: v })}>
                <SelectTrigger data-testid="contact-form-channel"><SelectValue placeholder="Pilih channel" /></SelectTrigger>
                <SelectContent>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <FormField label="Nama" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-form-name" />
          <FormField label="Nomor WhatsApp *" value={form.phone_number} onChange={(v) => setForm({ ...form, phone_number: v })} placeholder="+628..." testid="contact-form-phone" />
          <FormField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-form-email" />
          <FormField label="Perusahaan" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="contact-form-company" />
          <FormField label="Tags (pisahkan koma)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="contact-form-tags" />
          <div className="space-y-2">
            <Label>Status Opt-in</Label>
            <Select value={form.opt_in_status} onValueChange={(v) => setForm({ ...form, opt_in_status: v })}>
              <SelectTrigger data-testid="contact-form-optin"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unknown">Unknown</SelectItem>
                <SelectItem value="opted_in">Opt-in</SelectItem>
                <SelectItem value="opted_out">Opt-out</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Catatan</Label>
            <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-form-notes" />
          </div>
        </div>
      </RightDrawer>
    </div>
  );
}

export const FormField = ({ label, value, onChange, placeholder, testid, type = "text" }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
  </div>
);
